<template>
    <div class="modal" ref="modalElement">
      <h3>{{ isEditing ? 'Edit RssNode' : 'Add New RssNode' }}</h3>
      <form @submit.prevent="submitForm">
        <div v-for="(field, index) in formFields" :key="index" class="form-group">
          <label :for="field.name">{{ field.label }}</label>
          <input
            v-model="form[field.name]"
            :type="field.type"
            :id="field.name"
            :placeholder="field.placeholder"
            required
          />
        </div>
        
        <!-- AI Settings Section -->
        <div class="ai-settings-section">
          <h4>AI 智能处理设置</h4>
          
          <div class="form-group">
            <label for="isAiEnabled">启用 AI 分析</label>
            <div class="toggle-wrapper">
              <input type="checkbox" id="isAiEnabled" v-model="form.isAiEnabled" />
              <span class="toggle-label">{{ form.isAiEnabled ? '已启用' : '已禁用' }}</span>
            </div>
          </div>
          
          <div class="form-group" v-if="form.isAiEnabled">
            <label for="aiKeywords">触发关键词 (逗号分隔)</label>
            <input
              v-model="form.aiKeywords"
              type="text"
              id="aiKeywords"
              placeholder="AI, Linux, 技术, 编程"
            />
            <small class="helper-text">标题包含这些关键词的文章才会被抓取全文。留空则处理所有文章。</small>
          </div>
          
          <div class="form-group" v-if="form.isAiEnabled">
            <label for="aiBlockwords">屏蔽词 (逗号分隔)</label>
            <input
              v-model="form.aiBlockwords"
              type="text"
              id="aiBlockwords"
              placeholder="广告, 招聘, 水贴"
            />
            <small class="helper-text">标题包含这些词的文章将被跳过。</small>
          </div>
          
          <div class="form-group" v-if="form.isAiEnabled">
            <label for="scoreThreshold">评分阈值 (0-100)</label>
            <input
              v-model.number="form.scoreThreshold"
              type="number"
              id="scoreThreshold"
              min="0"
              max="100"
              placeholder="60"
            />
            <small class="helper-text">只有评分 >= 此值的文章才会存入向量库。默认 60。</small>
          </div>
        </div>
        
        <button type="submit">{{ isEditing ? 'Update' : 'Submit' }}</button>
        <button type="button" @click="closeModal">Cancel</button>
      </form>
    </div>
  </template>
  
  <script>
  export default {
    props: {
      isEditing: {
        type: Boolean,
        default: false
      },
      entity: {
        type: Object,
        default: () => ({})
      },
      isVisible: {
        type: Boolean,
        default: false // Control visibility from the parent
      }
    },
    data() {
      return {
        formFields: [
          { name: 'groupId', label: 'Group ID', type: 'text', placeholder: 'Enter Group ID' },
          { name: 'siteUrl', label: 'Site URL', type: 'text', placeholder: 'Enter Site URL' },
          { name: 'siteName', label: 'Site Name', type: 'text', placeholder: 'Enter Site Name' },
          { name: 'apiHost', label: 'API Host', type: 'text', placeholder: 'Enter API Host' },
          { name: 'siteIcon', label: 'Site Icon', type: 'text', placeholder: 'Enter Site Icon URL' },
          { name: 'lastUpdate', label: 'Last Update', type: 'date', placeholder: '' },
          { name: 'isFirst', label: 'Is First', type: 'number', placeholder: 'Enter 0 or 1' },
          { name: 'isEnable', label: 'Is Eanble', type: 'number', placeholder: 'Enter 0 or 1' },
          { name: 'updateInterval', label: 'Update Interval', type: 'number', placeholder: 'Enter update interval in minutes' },
          { name: 'sortId', label: 'Sort ID', type: 'number', placeholder: 'Enter sort order (lower first)' }
        ],
        form: {
          groupId: '',
          siteUrl: '',
          siteName: '',
          apiHost: '',
          siteIcon: '',
          lastUpdate: '',
          isFirst: 0,
          isEnable: 0,
          updateInterval: 0,
          sortId: 0,
          // AI Settings
          isAiEnabled: false,
          aiKeywords: '',
          aiBlockwords: '',
          scoreThreshold: 60
        }
      };
    },
    mounted() {
      this.showModal(); // Call showModal on mount
    },
    watch: {
      // Watch for changes in the entity prop to update the form
      entity: {
        handler(newEntity) {
          if (this.isEditing) {
            let form = { ...newEntity };
            if (form.lastUpdate) {
              form.lastUpdate = form.lastUpdate.substring(0, 10);
            }
            // Parse ValidationRules JSON to populate AI fields
            form.isAiEnabled = form.isAiEnabled === 1 || form.isAiEnabled === true;
            if (form.validationRules) {
              try {
                const rules = JSON.parse(form.validationRules);
                form.aiKeywords = (rules.Keywords || []).join(', ');
                form.aiBlockwords = (rules.Blockwords || []).join(', ');
                form.scoreThreshold = rules.ScoreThreshold || 60;
              } catch (e) {
                form.aiKeywords = '';
                form.aiBlockwords = '';
                form.scoreThreshold = 60;
              }
            } else {
              form.aiKeywords = '';
              form.aiBlockwords = '';
              form.scoreThreshold = 60;
            }
            this.form = form;
          }
        },
        immediate: true // Trigger the watcher immediately
      },
      isVisible: {
        handler(newVal) {
          if (!newVal) {
            this.closeModal(); // Call closeModal if isVisible changes to false
          } else {
            this.showModal(); // Call showModal if isVisible changes to true
          }
        },
        immediate: true // Trigger the watcher immediately
      }
    },
    methods: {
      showModal() {
        const modalElement = this.$refs.modalElement;
        if (modalElement && window.getComputedStyle(modalElement).display === 'none') {
          modalElement.style.display = 'block'; // Show the modal
        }
      },
      closeModal() {
        const modalElement = this.$refs.modalElement;
        if (modalElement) {
          modalElement.style.display = 'none'; // Hide the modal
        }
        this.$emit('closeModal'); // Emit close modal event
      },
      submitForm() {
        console.log('Form Submitted:', this.form);
        // Build ValidationRules JSON from AI fields
        const validationRules = {
          Keywords: this.form.aiKeywords ? this.form.aiKeywords.split(',').map(s => s.trim()).filter(s => s) : [],
          Blockwords: this.form.aiBlockwords ? this.form.aiBlockwords.split(',').map(s => s.trim()).filter(s => s) : [],
          ScoreThreshold: this.form.scoreThreshold || 60
        };
        const payload = {
          ...this.form,
          isAiEnabled: this.form.isAiEnabled ? 1 : 0,
          validationRules: JSON.stringify(validationRules)
        };
        // Remove temporary fields
        delete payload.aiKeywords;
        delete payload.aiBlockwords;
        delete payload.scoreThreshold;
        this.$emit(this.isEditing ? 'updateRssNode' : 'addRssNode', payload);
        this.closeModal(); // Close modal after submission
      }
    }
  };
  </script>
  
  <style scoped>
  .modal {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 9999; /* Ensure it is on top */
    background-color: white;
    padding: 20px;
    border: 1px solid #ccc;
  }
  .form-group {
    margin-bottom: 15px;
  }
  input {
    width: 100%;
    padding: 8px;
    margin-top: 5px;
  }
  
  .ai-settings-section {
    margin-top: 20px;
    padding: 15px;
    background-color: #f8f9fa;
    border-radius: 8px;
    border: 1px solid #e9ecef;
  }
  
  .ai-settings-section h4 {
    margin: 0 0 15px 0;
    font-size: 1rem;
    color: #495057;
    border-bottom: 1px solid #dee2e6;
    padding-bottom: 8px;
  }
  
  .toggle-wrapper {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  
  .toggle-wrapper input[type="checkbox"] {
    width: auto;
    transform: scale(1.3);
  }
  
  .toggle-label {
    font-weight: 500;
    color: #28a745;
  }
  
  .helper-text {
    display: block;
    margin-top: 4px;
    font-size: 0.75rem;
    color: #6c757d;
  }
  </style>
  